import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Phone, KeyRound, Smartphone } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { FundleLogo } from "../components/Brand";

export default function Login() {
    const navigate = useNavigate();
    const [sp] = useSearchParams();
    const nextUrl = sp.get("next") || "/account";
    const { refresh } = useAuth();
    const [step, setStep] = useState("mobile");
    const [mobile, setMobile] = useState("");
    const [name, setName] = useState("");
    const [code, setCode] = useState("");
    const [hint, setHint] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const requestOtp = async () => {
        if (!/^\d{10}$/.test(mobile)) {
            setErr("Please enter a valid 10-digit mobile number");
            return;
        }
        setBusy(true);
        setErr("");
        try {
            const r = await api.post("/api/auth/otp/request", { mobile });
            setHint(r.data.demo_otp ? `Demo OTP: ${r.data.demo_otp}` : "OTP sent");
            setStep("otp");
        } catch (e) {
            setErr(e.response?.data?.detail || "Could not send OTP");
        } finally {
            setBusy(false);
        }
    };

    const verifyOtp = async () => {
        if (code.length < 4) {
            setErr("Enter the OTP");
            return;
        }
        setBusy(true);
        setErr("");
        try {
            const r = await api.post("/api/auth/otp/verify", { mobile, code, name });
            localStorage.setItem("fx_token", r.data.token);
            await refresh();
            navigate(nextUrl);
        } catch (e) {
            setErr(e.response?.data?.detail || "Verification failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-white text-[#0b1437] fx-grad-hero flex flex-col">
            <header className="px-5 md:px-10 py-5 flex items-center justify-between">
                <Link to="/"><FundleLogo /></Link>
                <a
                    href="https://wa.me/918072905306?text=Hi"
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-[#25D366] flex items-center gap-1"
                >
                    <Smartphone size={14} /> WhatsApp
                </a>
            </header>

            <main className="flex-1 flex items-center justify-center p-5">
                <div className="w-full max-w-md fx-glass rounded-3xl p-7 fx-anim-rise">
                    <span className="fx-chip bg-[#c9a959]/10 text-[#c9a959] border border-[#c9a959]/30">
                        Welcome to Fundle
                    </span>
                    <h1 className="font-display text-3xl font-extrabold mt-3">
                        {step === "mobile" ? "Let's get you in." : "Verify your number."}
                    </h1>
                    <p className="text-[#6b7280] mt-2 text-sm">
                        {step === "mobile"
                            ? "Use your mobile number. We'll send a one-time OTP."
                            : `OTP sent to +91 ${mobile}.`}
                    </p>

                    {step === "mobile" ? (
                        <div className="mt-7 space-y-3">
                            <label className="text-xs uppercase tracking-widest text-[#6b7280]">Mobile</label>
                            <div className="flex items-center gap-2 fx-input">
                                <Phone size={16} className="text-[#c9a959]" />
                                <span className="text-[#4b5563]">+91</span>
                                <input
                                    data-testid="login-mobile-input"
                                    value={mobile}
                                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                                    placeholder="98765 43210"
                                    className="flex-1 bg-transparent outline-none placeholder-[#9ca3af]"
                                />
                            </div>
                            <label className="text-xs uppercase tracking-widest text-[#6b7280] mt-4 block">
                                Name (optional)
                            </label>
                            <input
                                data-testid="login-name-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Your name"
                                className="fx-input"
                            />
                            {err && <p className="text-red-400 text-sm">{err}</p>}
                            <button
                                data-testid="send-otp-btn"
                                onClick={requestOtp}
                                disabled={busy}
                                className="fx-btn-primary w-full mt-3"
                            >
                                {busy ? "Sending…" : "Send OTP"} <ArrowRight size={16} />
                            </button>
                        </div>
                    ) : (
                        <div className="mt-7 space-y-3">
                            <label className="text-xs uppercase tracking-widest text-[#6b7280]">OTP</label>
                            <div className="flex items-center gap-2 fx-input">
                                <KeyRound size={16} className="text-[#c9a959]" />
                                <input
                                    data-testid="login-otp-input"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    placeholder="6-digit code"
                                    className="flex-1 bg-transparent outline-none placeholder-[#9ca3af] tracking-widest"
                                />
                            </div>
                            {hint && <p className="text-[#c9a959] text-xs">{hint}</p>}
                            {err && <p className="text-red-400 text-sm">{err}</p>}
                            <button
                                data-testid="verify-otp-btn"
                                onClick={verifyOtp}
                                disabled={busy}
                                className="fx-btn-primary w-full mt-3"
                            >
                                {busy ? "Verifying…" : "Verify & Continue"} <ArrowRight size={16} />
                            </button>
                            <button
                                onClick={() => setStep("mobile")}
                                className="text-[#6b7280] text-sm w-full text-center mt-2 hover:text-[#0b1437]"
                            >
                                Change number
                            </button>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
